import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Image as ImageIcon, Instagram, LogOut, Trash2, Check, Plus, ExternalLink, ArrowLeft, AlertTriangle, X } from "lucide-react";
import { useAuth, authHeaders, API } from "../context/AuthContext";

const tabs = [
  { id: "messages", label: "Mensagens", icon: Mail },
  { id: "portfolio", label: "Portfólio", icon: ImageIcon },
  { id: "instagram", label: "Instagram", icon: Instagram },
];

const TabButton = ({ active, onClick, icon: Icon, label, testId, count }) => (
  <button
    onClick={onClick}
    className={`w-full text-left flex items-center justify-between px-4 py-4 border-l-2 transition-colors duration-200 font-heading tracking-[0.25em] uppercase text-xs ${
      active ? "border-[#c0392b] text-[#c0392b] bg-white/5" : "border-transparent text-white/70 hover:text-white"
    }`}
    data-testid={testId}
  >
    <span className="flex items-center gap-3">
      <Icon size={16} />
      {label}
    </span>
    {typeof count === "number" && (
      <span className="text-white/40 text-[10px]">{count}</span>
    )}
  </button>
);

const MessagesTab = ({ items, onReload }) => {
  const markRead = async (id) => {
    try {
      await axios.patch(`${API}/admin/messages/${id}/read`, {}, { headers: authHeaders(), withCredentials: true });
      onReload();
    } catch { toast.error("Erro ao marcar como lida."); }
  };
  const del = async (id) => {
    try {
      await axios.delete(`${API}/admin/messages/${id}`, { headers: authHeaders(), withCredentials: true });
      toast.success("Mensagem eliminada.");
      onReload();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erro ao eliminar. Verifica a sessão.");
    }
  };
  if (items.length === 0) {
    return <div className="text-white/50 font-heading tracking-[0.25em] uppercase text-sm py-16 text-center" data-testid="messages-empty">Sem mensagens.</div>;
  }
  return (
    <div className="space-y-4" data-testid="messages-list">
      {items.map((m, i) => (
        <div key={m.id} className={`border ${m.read ? "border-white/15" : "border-[#c0392b]"} p-6`} data-testid={`message-card-${i}`}>
          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
            <div>
              <div className="font-heading text-white tracking-[0.2em] uppercase">{m.name}</div>
              <a href={`mailto:${m.email}`} className="text-white/60 hover:text-[#c0392b] text-sm transition-colors">{m.email}</a>
            </div>
            <div className="text-right">
              <span className="font-heading text-[#c0392b] tracking-[0.25em] text-xs uppercase">{m.project_type}</span>
              <div className="text-white/40 text-xs mt-1">{new Date(m.created_at).toLocaleString("pt-PT")}</div>
            </div>
          </div>
          <p className="text-white/85 leading-relaxed mb-5">{m.message}</p>
          <div className="flex gap-3">
            {!m.read && (
              <button onClick={() => markRead(m.id)} className="cta-secondary inline-flex items-center gap-2 text-xs" data-testid={`message-read-${i}`}>
                <Check size={14} /> Marcar Lida
              </button>
            )}
            <button onClick={() => del(m.id)} className="cta-secondary inline-flex items-center gap-2 text-xs hover:!border-[#c0392b] hover:!text-[#c0392b]" data-testid={`message-delete-${i}`}>
              <Trash2 size={14} /> Eliminar
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

const PortfolioImage = ({ src, alt }) => {
  const [err, setErr] = useState(false);
  if (err) {
    return (
      <div className="w-full h-44 flex flex-col items-center justify-center bg-black border border-[#c0392b]/60 text-[#c0392b] gap-2">
        <AlertTriangle size={18} />
        <span className="font-heading text-[10px] tracking-[0.25em] uppercase">URL Inválido</span>
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className="w-full h-44 object-cover"
      style={{ filter: "grayscale(100%)" }}
      onError={() => setErr(true)}
    />
  );
};

const PortfolioTab = ({ items, onReload }) => {
  const [form, setForm] = useState({ url: "", category: "Ciclismo de Estrada", description: "", span: "normal", order: 0 });
  const isLikelyInvalid = (u) => /instagram\.com\/(p|reel|reels|stories)\//i.test(u);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.url.trim()) return toast.error("URL é obrigatório.");
    if (isLikelyInvalid(form.url)) {
      return toast.error("Esse link é uma página do Instagram, não uma imagem. Usa o URL direto da foto (.jpg/.png/.webp).");
    }
    try {
      await axios.post(`${API}/admin/portfolio`, form, { headers: authHeaders(), withCredentials: true });
      toast.success("Imagem adicionada.");
      setForm({ url: "", category: "Ciclismo de Estrada", description: "", span: "normal", order: 0 });
      onReload();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erro ao adicionar.");
    }
  };
  const del = async (id) => {
    try {
      await axios.delete(`${API}/admin/portfolio/${id}`, { headers: authHeaders(), withCredentials: true });
      toast.success("Imagem eliminada.");
      onReload();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erro ao eliminar. Verifica a sessão.");
    }
  };
  return (
    <div className="space-y-8" data-testid="portfolio-tab">
      <form onSubmit={submit} className="border border-white/15 p-6 space-y-4" data-testid="portfolio-form">
        <h3 className="font-heading text-white text-2xl uppercase tracking-[0.1em]">Adicionar Imagem</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="label-uppercase">URL Direto da Imagem</label>
            <input type="url" className="field" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://exemplo.com/foto.jpg" data-testid="portfolio-url" />
          </div>
          <div>
            <label className="label-uppercase">Categoria</label>
            <select className="field" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="portfolio-category">
              {["Ciclismo de Estrada", "Ciclocross", "Mountain Bike", "Trail Running & Atletismo", "Off-Road & Todo-o-Terreno", "Retratos Artísticos", "Preto & Branco"].map((c) => <option key={c} value={c} className="bg-black">{c}</option>)}
            </select>
          </div>
          <div>
            <label className="label-uppercase">Span</label>
            <select className="field" value={form.span} onChange={(e) => setForm({ ...form, span: e.target.value })} data-testid="portfolio-span">
              {["normal", "tall", "wide"].map((s) => <option key={s} value={s} className="bg-black">{s}</option>)}
            </select>
          </div>
          <div>
            <label className="label-uppercase">Descrição</label>
            <input className="field" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="portfolio-desc" />
          </div>
          <div>
            <label className="label-uppercase">Ordem</label>
            <input type="number" className="field" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value || "0", 10) })} data-testid="portfolio-order" />
          </div>
        </div>
        <div className="border-l-2 border-[#c0392b] pl-4 py-2 bg-white/[0.02]">
          <p className="text-white/85 text-sm leading-relaxed">
            <span className="font-heading text-[#c0392b] tracking-[0.25em] uppercase text-xs block mb-2">Atenção ao URL</span>
            Usa o URL <strong className="text-white">direto da imagem</strong> (acaba em <code className="text-[#c0392b]">.jpg</code>, <code className="text-[#c0392b]">.png</code> ou <code className="text-[#c0392b]">.webp</code>).
            Links como <code className="text-white/60">instagram.com/p/...</code> são páginas e não funcionam — vê o site Imgur, Cloudinary ou Unsplash.
          </p>
        </div>
        <button className="cta-primary inline-flex items-center gap-2" data-testid="portfolio-add"><Plus size={16} /> Adicionar</button>
      </form>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {items.map((it, i) => (
          <div key={it.id} className="relative border border-white/15 group" data-testid={`portfolio-admin-item-${i}`}>
            <PortfolioImage src={it.url} alt={it.description} />
            <div className="p-3 border-t border-white/15">
              <div className="font-heading text-xs tracking-[0.25em] uppercase text-[#c0392b]">{it.category}</div>
              <div className="text-white/70 text-xs mt-1 truncate">{it.description || "—"}</div>
            </div>
            <button
              onClick={() => del(it.id)}
              className="absolute top-2 right-2 bg-[#c0392b] hover:bg-white hover:text-[#c0392b] text-white p-2 transition-colors flex items-center justify-center"
              data-testid={`portfolio-delete-${i}`}
              aria-label="Eliminar imagem"
              title="Eliminar imagem"
            >
              <X size={16} strokeWidth={2.5} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const AdminImage = ({ src, alt = "" }) => {
  const [err, setErr] = useState(false);
  if (err) {
    return (
      <div className="aspect-square w-full flex flex-col items-center justify-center bg-black border border-[#c0392b]/60 text-[#c0392b] gap-1">
        <AlertTriangle size={14} />
        <span className="font-heading text-[9px] tracking-[0.2em] uppercase">URL Inválido</span>
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className="aspect-square w-full object-cover"
      style={{ filter: "grayscale(100%)" }}
      onError={() => setErr(true)}
    />
  );
};

const InstagramTab = ({ items, onReload }) => {
  const [url, setUrl] = useState("");
  const isLikelyInvalid = (u) => /instagram\.com\/(p|reel|reels|stories)\//i.test(u);
  const add = async (e) => {
    e.preventDefault();
    if (!url.trim()) return toast.error("URL é obrigatório.");
    if (isLikelyInvalid(url)) {
      return toast.error("Esse link é uma página do Instagram, não uma imagem. Usa o URL direto da foto (.jpg/.png/.webp).");
    }
    try {
      await axios.post(`${API}/admin/instagram`, { url, order: items.length }, { headers: authHeaders(), withCredentials: true });
      toast.success("Adicionado.");
      setUrl("");
      onReload();
    } catch { toast.error("Erro ao adicionar."); }
  };
  const del = async (id) => {
    try {
      await axios.delete(`${API}/admin/instagram/${id}`, { headers: authHeaders(), withCredentials: true });
      toast.success("Imagem eliminada.");
      onReload();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erro ao eliminar. Verifica a sessão.");
    }
  };
  return (
    <div className="space-y-8" data-testid="instagram-tab">
      <form onSubmit={add} className="border border-white/15 p-6 space-y-4">
        <div>
          <label className="label-uppercase">URL Direto da Imagem</label>
          <div className="flex flex-col md:flex-row gap-4 items-stretch md:items-end">
            <input type="url" className="field flex-1" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://exemplo.com/foto.jpg" data-testid="instagram-url" />
            <button className="cta-primary inline-flex items-center gap-2 justify-center" data-testid="instagram-add"><Plus size={16} /> Adicionar</button>
          </div>
        </div>
        <div className="border-l-2 border-[#c0392b] pl-4 py-2 bg-white/[0.02]">
          <p className="text-white/85 text-sm leading-relaxed">
            <span className="font-heading text-[#c0392b] tracking-[0.25em] uppercase text-xs block mb-2">Atenção</span>
            Tem de ser o URL <strong className="text-white">direto da imagem</strong> (termina em <code className="text-[#c0392b]">.jpg</code>, <code className="text-[#c0392b]">.png</code> ou <code className="text-[#c0392b]">.webp</code>).
            URLs como <code className="text-white/60">instagram.com/p/...</code> são páginas — <strong className="text-white">não funcionam</strong>.
            Faz upload da foto para um serviço como Imgur, Cloudinary ou usa a tua galeria do Unsplash, e cola aqui o link direto.
          </p>
        </div>
      </form>

      <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
        {items.map((it, i) => (
          <div key={it.id} className="relative border border-white/15 group" data-testid={`instagram-admin-item-${i}`}>
            <AdminImage src={it.url} />
            <button
              onClick={() => del(it.id)}
              className="absolute top-1 right-1 bg-[#c0392b] hover:bg-white hover:text-[#c0392b] text-white p-1.5 transition-colors flex items-center justify-center"
              data-testid={`instagram-delete-${i}`}
              aria-label="Eliminar foto"
              title="Eliminar foto"
            >
              <X size={14} strokeWidth={2.5} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const [active, setActive] = useState("messages");
  const [messages, setMessages] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [instagram, setInstagram] = useState([]);

  const load = useCallback(async () => {
    try {
      const [m, p, ig] = await Promise.all([
        axios.get(`${API}/admin/messages`, { headers: authHeaders(), withCredentials: true }),
        axios.get(`${API}/portfolio`),
        axios.get(`${API}/instagram`),
      ]);
      setMessages(m.data || []);
      setPortfolio(p.data || []);
      setInstagram(ig.data || []);
    } catch (e) {
      // Ignore — Auth guard handles 401
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const unread = messages.filter((m) => !m.read).length;

  return (
    <div className="admin-shell" data-testid="admin-dashboard">
      <div className="border-b border-white/15 sticky top-0 bg-black/95 backdrop-blur-sm z-10">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="font-heading text-white text-lg tracking-[0.25em] hover:text-[#c0392b] transition-colors" data-testid="admin-logo">
              MIGUEL FARIA<span className="text-[#c0392b]">.</span>
            </Link>
            <span className="hidden md:inline font-heading text-[#c0392b] tracking-[0.4em] text-xs uppercase">Painel</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" target="_blank" className="hidden md:inline-flex items-center gap-2 text-white/70 hover:text-[#c0392b] transition-colors font-heading text-xs tracking-[0.25em] uppercase" data-testid="admin-view-site">
              Ver Site <ExternalLink size={14} />
            </Link>
            <span className="hidden lg:inline text-white/60 text-sm">{user?.email}</span>
            <button onClick={logout} className="cta-secondary inline-flex items-center gap-2 text-xs" data-testid="admin-logout">
              <LogOut size={14} /> Sair
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <aside className="lg:col-span-3 border border-white/15">
          <Link to="/" className="flex items-center gap-2 px-4 py-3 text-white/60 hover:text-[#c0392b] transition-colors font-heading text-xs tracking-[0.25em] uppercase border-b border-white/15">
            <ArrowLeft size={14} /> Voltar
          </Link>
          {tabs.map((t) => (
            <TabButton
              key={t.id}
              active={active === t.id}
              onClick={() => setActive(t.id)}
              icon={t.icon}
              label={t.label}
              testId={`admin-tab-${t.id}`}
              count={t.id === "messages" ? unread : t.id === "portfolio" ? portfolio.length : instagram.length}
            />
          ))}
        </aside>

        <section className="lg:col-span-9">
          <h1 className="font-heading text-white text-5xl md:text-6xl uppercase mb-8 leading-[0.95]" data-testid="admin-section-title">
            {tabs.find((t) => t.id === active)?.label}
          </h1>
          {active === "messages" && <MessagesTab items={messages} onReload={load} />}
          {active === "portfolio" && <PortfolioTab items={portfolio} onReload={load} />}
          {active === "instagram" && <InstagramTab items={instagram} onReload={load} />}
        </section>
      </div>
    </div>
  );
};

export default AdminDashboard;
